import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, useConfirmDialog } from '@safetyhub/ui';
import {
  AI_MEASURE_TONE_PLACEHOLDER,
  resolveAiMeasureToneTemplateStatus,
} from '@safetyhub/shared';
import { adminApi } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';

// ============================================
// 공통 상수
// ============================================

const PROVIDER_INFO: Record<string, { name: string; description: string; defaultModel: string }> = {
  gemini: {
    name: 'Google Gemini',
    description: 'Gemini Vision - 이미지 분석 및 좌표 추출',
    defaultModel: 'gemini-3-flash-preview',
  },
  openai: {
    name: 'OpenAI',
    description: 'GPT-4 Vision을 통한 이미지 분석',
    defaultModel: 'gpt-4o-mini',
  },
  claude: {
    name: 'Anthropic Claude',
    description: 'Claude 3를 통한 이미지 분석 (한국어 우수)',
    defaultModel: 'claude-3-haiku-20240307',
  },
};

const PROMPT_TYPES: Record<string, { name: string; description: string }> = {
  HAZARD_ANALYSIS: {
    name: '위험요소 분석',
    description: '이미지에서 안전 위험요소를 식별',
  },
  TBM_GENERATION: {
    name: 'TBM 생성',
    description: '점검 결과로 TBM 내용 자동 생성',
  },
  REPORT_SUMMARY: {
    name: '보고서 요약',
    description: '점검 내용 요약문 생성',
  },
  FINDING_ENHANCEMENT: {
    name: '위험요소 상세화',
    description: '식별된 위험요소에 대한 상세 설명 생성',
  },
  KNOWLEDGE_RAG: {
    name: '지식베이스 RAG 매칭',
    description: '위험요인 후보 중 적합한 것을 LLM이 선별',
  },
  FORM_IMPORT: {
    name: '양식 업로드 구조 분석',
    description: '양식 이미지/PDF에서 Composer 템플릿 IR 추출',
  },
  FORM_FIELD_MAPPING: {
    name: '양식 필드 데이터 연결 매칭',
    description: '양식 입력 필드와 시스템 조회 데이터 매칭 후보 제안',
  },
};

// ============================================
// 타입 정의
// ============================================

interface AIProvider {
  id: string;
  provider: string;
  name: string | null; // 표시용 이름 (예: "Gemini Flash", "Gemini Pro")
  enabled: boolean;
  priority: number;
  apiKey: string;
  baseUrl: string | null;
  model: string | null;
  settings: Record<string, unknown> | null;
  usageTrackingMethod: 'direct' | 'provider_api';
  adminApiKey: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AIPrompt {
  id: string;
  name: string;
  type: string;
  industryType: string | null;
  systemPrompt: string;
  userPrompt: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

type TabType = 'providers' | 'prompts';

// ============================================
// 메인 컴포넌트
// ============================================

export default function AISettings() {
  const [activeTab, setActiveTab] = useState<TabType>('providers');

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">AI 설정</h2>
        <p className="mt-1 text-sm text-gray-500">
          AI 이미지 분석에 사용할 프로바이더와 프롬프트를 관리합니다.
        </p>
      </div>

      {/* 탭 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('providers')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'providers'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            프로바이더
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'prompts'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            프롬프트
          </button>
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'providers' ? <ProvidersTab /> : <PromptsTab />}
    </div>
  );
}

// ============================================
// 드래그 가능한 프로바이더 행 컴포넌트
// ============================================

interface SortableProviderRowProps {
  provider: AIProvider;
  index: number;
  onToggle: (id: string, enabled: boolean) => void;
  onTest: (provider: string) => void;
  onEdit: (provider: AIProvider) => void;
  onDelete: (id: string) => void;
  testingProvider: string | null;
  isToggling: boolean;
}

function SortableProviderRow({
  provider,
  index,
  onToggle,
  onTest,
  onEdit,
  onDelete,
  testingProvider,
  isToggling,
}: SortableProviderRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: provider.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f9fafb' : undefined,
  };

  // 행 클릭 핸들러 (버튼 영역 제외)
  const handleRowClick = (e: React.MouseEvent) => {
    // 버튼이나 인터랙티브 요소 클릭 시 무시
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    onEdit(provider);
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${isDragging ? 'shadow-lg' : ''} cursor-pointer hover:bg-gray-50`}
      onClick={handleRowClick}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          {/* 드래그 핸들 */}
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
            title="드래그하여 순서 변경"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </button>
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-medium">
            {index + 1}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <span className="font-medium text-gray-900">
            {provider.name || PROVIDER_INFO[provider.provider]?.name || provider.provider}
          </span>
          <p className="text-sm text-gray-500">
            {PROVIDER_INFO[provider.provider]?.description || provider.provider}
          </p>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
        {provider.model || PROVIDER_INFO[provider.provider]?.defaultModel || '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <button
          onClick={() => onToggle(provider.id, !provider.enabled)}
          disabled={isToggling}
          className={`px-3 py-1 rounded text-sm font-medium ${
            provider.enabled
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {provider.enabled ? '활성' : '비활성'}
        </button>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onTest(provider.provider)}
            disabled={testingProvider === provider.provider}
            className="text-brand-600 hover:text-brand-800 text-sm disabled:opacity-50"
          >
            {testingProvider === provider.provider ? '테스트 중...' : '연결 테스트'}
          </button>
          <button
            // 확인은 호출부(`ProvidersTab`)가 연다 — 확인 창을 이 행 안에서 그리면 그 창의 클릭이 React
            // 트리를 따라 행의 `handleRowClick`(편집 열기)로 번진다.
            onClick={() => onDelete(provider.id)}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            삭제
          </button>
        </div>
      </td>
    </tr>
  );
}

// ============================================
// 프로바이더 탭
// ============================================

function ProvidersTab() {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<AIProvider | null>(null);
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ provider: string; connected: boolean } | null>(null);

  // dnd-kit 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px 이동 후 드래그 시작
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'ai-providers'],
    queryFn: () => adminApi.getAIProviders(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteAIProvider(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-providers'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      adminApi.updateAIProvider(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-providers'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: (provider: string) => adminApi.testAIProvider(provider),
    onMutate: (provider) => {
      setTestingProvider(provider);
      setTestResult(null);
    },
    onSuccess: (result) => {
      if (result.success && result.data) {
        setTestResult(result.data);
      }
      setTestingProvider(null);
    },
    onError: () => {
      setTestingProvider(null);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) => adminApi.reorderAIProviders(orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-providers'] });
    },
  });

  // 드래그 종료 핸들러
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const sortedProviders = [...(data?.data || [])].sort((a, b) => a.priority - b.priority);
      const oldIndex = sortedProviders.findIndex((p) => p.id === active.id);
      const newIndex = sortedProviders.findIndex((p) => p.id === over.id);

      const newOrder = arrayMove(sortedProviders, oldIndex, newIndex);
      reorderMutation.mutate(newOrder.map((p) => p.id));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const providers = data.data || [];
  const sortedProviders = [...providers].sort((a, b) => a.priority - b.priority);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          드래그하여 우선순위를 변경하세요. 장애 시 자동으로 전환됩니다.
        </p>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <span>+</span>
          <span>프로바이더 추가</span>
        </Button>
      </div>

      {/* 테스트 결과 알림 */}
      {testResult && (
        <div
          className={`mb-4 p-4 rounded ${
            testResult.connected ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          <span className="font-medium">{PROVIDER_INFO[testResult.provider]?.name || testResult.provider}</span>
          {testResult.connected ? ' 연결 테스트 성공' : ' 연결 테스트 실패'}
          <button
            onClick={() => setTestResult(null)}
            className="ml-4 text-sm underline"
          >
            닫기
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Card padding="none" radius="lg" className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  우선순위
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  프로바이더
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  모델
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProviders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    등록된 AI 프로바이더가 없습니다.
                  </td>
                </tr>
              ) : (
                <SortableContext
                  items={sortedProviders.map((p) => p.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {sortedProviders.map((provider, index) => (
                    <SortableProviderRow
                      key={provider.id}
                      provider={provider}
                      index={index}
                      onToggle={(id, enabled) => toggleMutation.mutate({ id, enabled })}
                      onTest={(p) => testMutation.mutate(p)}
                      onEdit={setEditTarget}
                      onDelete={async (id) => {
                        if (
                          await confirm({
                            title: '프로바이더 삭제',
                            body: '이 프로바이더를 삭제하시겠습니까?',
                            confirmLabel: '삭제',
                            danger: true,
                          })
                        ) {
                          deleteMutation.mutate(id);
                        }
                      }}
                      testingProvider={testingProvider}
                      isToggling={toggleMutation.isPending}
                    />
                  ))}
                </SortableContext>
              )}
            </tbody>
          </table>
        </Card>
      </DndContext>

      <p className="mt-4 text-sm text-gray-500">
        총 {providers.length}개의 프로바이더 ({providers.filter((p) => p.enabled).length}개 활성)
      </p>

      {/* 추가 모달 */}
      {showAddModal && (
        <ProviderModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-providers'] });
            setShowAddModal(false);
          }}
        />
      )}

      {/* 수정 모달 */}
      {editTarget && (
        <ProviderModal
          provider={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-providers'] });
            setEditTarget(null);
          }}
        />
      )}
      {confirmDialog}
    </div>
  );
}

// ============================================
// 프롬프트 탭
// ============================================

function PromptsTab() {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState<AIPrompt | null>(null);
  const [viewTarget, setViewTarget] = useState<AIPrompt | null>(null);
  const [viewHistoryTarget, setViewHistoryTarget] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'ai-prompts'],
    queryFn: () => adminApi.getAIPrompts(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteAIPrompt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-prompts'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const prompts = data.data || [];
  const activePrompts = prompts.filter((p) => p.isActive);
  const inactivePrompts = prompts.filter((p) => !p.isActive);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          버전 이력이 자동 저장됩니다.
        </p>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <span>+</span>
          <span>프롬프트 추가</span>
        </Button>
      </div>

      {/* 활성 프롬프트 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">활성 프롬프트</h3>
        <div className="grid gap-4 md:grid-cols-2">
          {activePrompts.length === 0 ? (
            <div className="col-span-2 bg-gray-50 p-6 rounded text-center text-gray-500">
              활성화된 프롬프트가 없습니다.
            </div>
          ) : (
            activePrompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                onView={() => setViewTarget(prompt)}
                onEdit={() => setEditTarget(prompt)}
                onDelete={async () => {
                  if (
                    await confirm({
                      title: '프롬프트 비활성화',
                      body: '이 프롬프트를 비활성화하시겠습니까?',
                      confirmLabel: '비활성화',
                      // 행은 비활성 목록에 남지만 쓰이던 프롬프트가 곧바로 빠지는 운영 동작이라 danger로 둔다.
                      danger: true,
                    })
                  ) {
                    deleteMutation.mutate(prompt.id);
                  }
                }}
                onViewHistory={() => setViewHistoryTarget(prompt.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* 비활성 프롬프트 */}
      {inactivePrompts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">비활성 프롬프트</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {inactivePrompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                onView={() => setViewTarget(prompt)}
                onEdit={() => setEditTarget(prompt)}
                onViewHistory={() => setViewHistoryTarget(prompt.id)}
                inactive
              />
            ))}
          </div>
        </div>
      )}

      {/* 추가 모달 */}
      {showAddModal && (
        <PromptModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-prompts'] });
            setShowAddModal(false);
          }}
        />
      )}

      {/* 수정 모달 */}
      {editTarget && (
        <PromptModal
          prompt={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-prompts'] });
            setEditTarget(null);
          }}
        />
      )}

      {/* 이력 모달 */}
      {viewHistoryTarget && (
        <HistoryModal
          promptId={viewHistoryTarget}
          onClose={() => setViewHistoryTarget(null)}
          onRestore={() => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'ai-prompts'] });
            setViewHistoryTarget(null);
          }}
        />
      )}

      {/* 상세보기 모달 */}
      {viewTarget && (
        <PromptDetailModal
          prompt={viewTarget}
          onClose={() => setViewTarget(null)}
          onEdit={() => {
            setViewTarget(null);
            setEditTarget(viewTarget);
          }}
        />
      )}
      {confirmDialog}
    </div>
  );
}

// ============================================
// 프로바이더 모달
// ============================================

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function ProviderModal({
  provider,
  onClose,
  onSuccess,
}: {
  provider?: AIProvider;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [providerType, setProviderType] = useState(provider?.provider || 'gemini');
  const [name, setName] = useState(provider?.name || '');
  // 복수 API 키 지원 - 목록으로 관리
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [newApiKey, setNewApiKey] = useState('');
  const [model, setModel] = useState(provider?.model || '');
  // 양식 분석(form-import) 전용 모델 override — settings.formImportModel에 저장.
  // 비워 두면 위 모델을 그대로 쓴다(사진 분석 등 다른 호출은 항상 위 모델).
  const [formImportModel, setFormImportModel] = useState(
    typeof provider?.settings?.formImportModel === 'string' ? provider.settings.formImportModel : ''
  );
  // 양식 분석 전용 thinking 예산 — settings.formImportThinkingLevel에 저장.
  // 비워 두면 서버 기본값(low)을 쓴다. 값이 곧 API의 thinkingLevel이라 low|medium|high만 허용.
  const [formImportThinkingLevel, setFormImportThinkingLevel] = useState(
    typeof provider?.settings?.formImportThinkingLevel === 'string'
      ? provider.settings.formImportThinkingLevel
      : ''
  );
  const [baseUrl, setBaseUrl] = useState(provider?.baseUrl || '');
  const [enabled, setEnabled] = useState(provider?.enabled ?? true);
  const [usageTrackingMethod, setUsageTrackingMethod] = useState<'direct' | 'provider_api'>(
    provider?.usageTrackingMethod || 'direct'
  );
  const [adminApiKey, setAdminApiKey] = useState('');
  const [error, setError] = useState('');
  const [usageApiTestResult, setUsageApiTestResult] = useState<boolean | null>(null);
  const [isTestingUsageApi, setIsTestingUsageApi] = useState(false);
  const formId = useId();

  const isEdit = !!provider;

  // API 키 추가
  const handleAddApiKey = () => {
    const trimmedKey = newApiKey.trim();
    if (trimmedKey && !apiKeys.includes(trimmedKey)) {
      setApiKeys([...apiKeys, trimmedKey]);
      setNewApiKey('');
    }
  };

  // API 키 삭제
  const handleRemoveApiKey = (index: number) => {
    setApiKeys(apiKeys.filter((_, i) => i !== index));
  };

  // 키 입력 엔터키 처리
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddApiKey();
    }
  };

  const mutation = useMutation({
    mutationFn: () => {
      // 복수 API 키를 쉼표로 구분된 문자열로 변환
      const apiKeyString = apiKeys.length > 0 ? apiKeys.join(',') : undefined;

      // settings는 기존 키 보존 병합 — 양식 분석 override만 갱신(빈 값이면 제거해 해제)
      const nextSettings: Record<string, unknown> = { ...(provider?.settings || {}) };
      const trimmedFormImportModel = formImportModel.trim();
      if (trimmedFormImportModel) {
        nextSettings.formImportModel = trimmedFormImportModel;
      } else {
        delete nextSettings.formImportModel;
      }
      if (formImportThinkingLevel) {
        nextSettings.formImportThinkingLevel = formImportThinkingLevel;
      } else {
        delete nextSettings.formImportThinkingLevel;
      }

      const data = {
        settings: nextSettings,
        name: name || undefined,
        enabled,
        usageTrackingMethod,
        // priority는 자동 배치 (신규) 또는 드래그로 변경
        ...(apiKeyString && { apiKey: apiKeyString }),
        ...(model && { model }),
        ...(baseUrl && { baseUrl }),
        ...(adminApiKey && { adminApiKey }),
      };

      if (isEdit) {
        return adminApi.updateAIProvider(provider.id, data);
      } else {
        return adminApi.createAIProvider({
          provider: providerType,
          apiKey: apiKeyString || '',
          ...data,
        });
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error?.message || '저장에 실패했습니다.');
      }
    },
    onError: () => {
      setError('저장 중 오류가 발생했습니다.');
    },
  });

  const handleTestUsageApi = async () => {
    if (!provider?.id) return;
    setIsTestingUsageApi(true);
    setUsageApiTestResult(null);
    try {
      const result = await adminApi.testProviderUsageApi(provider.id);
      if (result.success && result.data) {
        setUsageApiTestResult(result.data.usageApiConnected);
      } else {
        setUsageApiTestResult(false);
      }
    } catch {
      setUsageApiTestResult(false);
    } finally {
      setIsTestingUsageApi(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isEdit && apiKeys.length === 0) {
      setError('최소 1개의 API 키를 입력해주세요.');
      return;
    }

    mutation.mutate();
  };

  // 미저장 닫기 보호 — 편집 가능한 입력 전수를 고정 키 순서로 직렬화해 첫 렌더 값과 비교한다.
  // 초기값이 전부 props에서 동기로 나오므로 첫 렌더가 곧 기준선이다(열자마자 dirty가 되지 않는다).
  const snapshot = JSON.stringify({
    providerType,
    name,
    apiKeys,
    newApiKey: newApiKey.trim(),
    model,
    formImportModel,
    formImportThinkingLevel,
    baseUrl,
    enabled,
    usageTrackingMethod,
    adminApiKey,
  });
  const [initialSnapshot] = useState(snapshot);
  const isDirty = snapshot !== initialSnapshot;

  return (
    // 부모가 열 때만 마운트하므로 open은 늘 true다. 제출 버튼이 base footer(폼 밖)에 있어 `form` 속성으로 묶는다.
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'AI 프로바이더 수정' : 'AI 프로바이더 추가'}
      size="md"
      isDirty={isDirty}
      footer={
        <>
          <ModalCancelButton onCancel={onClose} />
          <Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </>
      }
    >
        <form id={formId} onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              프로바이더 타입
            </label>
            <select
              value={providerType}
              onChange={(e) => setProviderType(e.target.value)}
              disabled={isEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
            >
              <option value="gemini">Google Gemini</option>
              <option value="openai">OpenAI</option>
              <option value="claude">Anthropic Claude</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              같은 타입으로 여러 개 등록 가능 (예: Gemini Flash, Gemini Pro)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              표시 이름 (선택)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: Gemini Flash, GPT-4o Mini"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API 키 {isEdit && '(변경 시에만 입력)'}
            </label>

            {/* 현재 등록된 API 키 목록 */}
            {apiKeys.length > 0 && (
              <div className="mb-2 space-y-2">
                {apiKeys.map((key, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded"
                  >
                    <span className="text-xs text-gray-400 font-medium w-6">#{index + 1}</span>
                    <span className="flex-1 font-mono text-sm text-gray-600">
                      {key.substring(0, 10)}...{key.substring(key.length - 4)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveApiKey(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      삭제
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 새 API 키 입력 */}
            <div className="flex gap-2">
              <input
                type="password"
                value={newApiKey}
                onChange={(e) => setNewApiKey(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isEdit && apiKeys.length === 0 ? '변경할 키 입력 (Enter로 추가)' : 'API 키 입력 (Enter로 추가)'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <button
                type="button"
                onClick={handleAddApiKey}
                disabled={!newApiKey.trim()}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                추가
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              여러 키를 등록하면 라운드 로빈으로 분산되어 rate limit을 효과적으로 사용할 수 있습니다.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              모델 (선택사항)
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={PROVIDER_INFO[providerType]?.defaultModel || '기본 모델 사용'}
              autoComplete="off"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              양식 분석 모델 (선택사항)
            </label>
            <input
              type="text"
              value={formImportModel}
              onChange={(e) => setFormImportModel(e.target.value)}
              placeholder="비워 두면 위 모델 사용 (예: gemini-3.1-pro)"
              autoComplete="off"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Composer 양식 업로드 분석에만 쓰는 모델입니다. 사진 분석 등 다른 호출은 위 모델을
              그대로 사용합니다. 양식 분석은 호출 빈도가 낮아 상위 모델을 써도 비용 부담이 적습니다.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              양식 분석 사고 예산 (Gemini 전용)
            </label>
            <select
              value={formImportThinkingLevel}
              onChange={(e) => setFormImportThinkingLevel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">기본 (low)</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              모델이 답을 내기 전에 &quot;생각&quot;하는 데 쓰는 예산입니다. 높일수록 정확도가 오를
              수 있으나 느려집니다 — 같은 양식·프롬프트 실측에서 Pro 기준 low는 23초, 예산을 풀어
              두면 78초가 걸렸고 결과 분량은 사실상 같았습니다. 양식 분석 호출에만 적용되며,
              Gemini 외 프로바이더는 이 값을 무시합니다.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base URL (선택사항)
            </label>
            <input
              type="text"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="커스텀 엔드포인트 (Azure 등)"
              autoComplete="off"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="enabled" className="text-sm text-gray-700">
              활성화
            </label>
          </div>

          {/* 사용량 추적 방식 설정 */}
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              사용량 추적 방식
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="usageTracking"
                  value="direct"
                  checked={usageTrackingMethod === 'direct'}
                  onChange={() => setUsageTrackingMethod('direct')}
                  className="mt-0.5"
                />
                <div>
                  <span className="font-medium text-gray-900">직접 계산 (기본값)</span>
                  <p className="text-sm text-gray-500">자체 DB 기반으로 토큰 사용량과 비용을 계산합니다.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="usageTracking"
                  value="provider_api"
                  checked={usageTrackingMethod === 'provider_api'}
                  onChange={() => setUsageTrackingMethod('provider_api')}
                  className="mt-0.5"
                  disabled={providerType !== 'openai' && providerType !== 'gemini'}
                />
                <div className={providerType !== 'openai' && providerType !== 'gemini' ? 'opacity-50' : ''}>
                  <span className="font-medium text-gray-900">프로바이더 API</span>
                  <p className="text-sm text-gray-500">
                    {providerType === 'openai'
                      ? 'OpenAI Usage API를 통해 실제 사용량을 조회합니다.'
                      : providerType === 'gemini'
                        ? 'GCP Cloud Monitoring API를 통해 사용량을 조회합니다.'
                        : '현재 OpenAI, Gemini만 지원됩니다.'}
                  </p>
                  {isEdit && provider?.id && usageTrackingMethod === 'provider_api' && (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleTestUsageApi}
                        disabled={isTestingUsageApi}
                        className="text-sm text-brand-600 hover:text-brand-800 disabled:opacity-50"
                      >
                        {isTestingUsageApi ? '테스트 중...' : '연결 테스트'}
                      </button>
                      {usageApiTestResult !== null && (
                        <span className={`text-sm ${usageApiTestResult ? 'text-green-600' : 'text-red-600'}`}>
                          {usageApiTestResult ? '✓ 연결됨' : '✗ 연결 실패'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>

          {/* Admin API Key (GCP 서비스 계정 등 별도 관리자 키 필요 시) */}
          {usageTrackingMethod === 'provider_api' && providerType === 'gemini' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GCP 서비스 계정 키 (JSON) {isEdit && '(변경 시에만 입력)'}
              </label>
              <textarea
                value={adminApiKey}
                onChange={(e) => setAdminApiKey(e.target.value)}
                placeholder={isEdit ? '********' : '서비스 계정 JSON 전체를 붙여넣으세요'}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-xs"
                rows={6}
              />
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                <p className="font-medium mb-1">필요한 GCP IAM 권한:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li><strong>Billing Account Viewer</strong> - 결제 정보 조회</li>
                  <li><strong>Monitoring Viewer</strong> - API 요청 수 조회</li>
                </ul>
                <p className="mt-2 text-amber-600">
                  * 토큰/비용 상세는 BigQuery export 필요 (현재 요청 수만 조회됨)
                </p>
              </div>
            </div>
          )}
        </form>
    </Modal>
  );
}

// ============================================
// 프롬프트 카드
// ============================================

function PromptCard({
  prompt,
  onView,
  onEdit,
  onDelete,
  onViewHistory,
  inactive,
}: {
  prompt: AIPrompt;
  onView: () => void;
  onEdit: () => void;
  onDelete?: () => void;
  onViewHistory: () => void;
  inactive?: boolean;
}) {
  return (
    <div
      onClick={onView}
      className={`bg-white rounded-card shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${
        inactive ? 'opacity-60 border border-gray-300' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900">{prompt.name}</h4>
            <span className="px-2 py-0.5 text-xs bg-brand-100 text-brand-700 rounded">
              v{prompt.version}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {PROMPT_TYPES[prompt.type]?.name || prompt.type}
            {prompt.industryType && ` (${prompt.industryType})`}
          </p>
        </div>
        {!inactive && (
          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
            활성
          </span>
        )}
      </div>

      <div className="mt-3 text-sm text-gray-600">
        <p className="line-clamp-2">{prompt.systemPrompt.substring(0, 100)}...</p>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-gray-400">
          {new Date(prompt.updatedAt).toLocaleDateString('ko-KR')} 수정
        </span>
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button onClick={onViewHistory} className="text-brand-600 hover:text-brand-800">
            이력
          </button>
          <button onClick={onEdit} className="text-gray-600 hover:text-gray-800">
            수정
          </button>
          {onDelete && (
            <button onClick={onDelete} className="text-red-600 hover:text-red-800">
              비활성화
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 프롬프트 모달
// ============================================

// export는 미저장 닫기 spec(`__tests__/formModalDirtyClose.test.tsx`)이 이 모달만 띄우기 위함이다.
export function PromptModal({
  prompt,
  onClose,
  onSuccess,
}: {
  prompt?: AIPrompt;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(prompt?.name || '');
  const [type, setType] = useState(prompt?.type || 'HAZARD_ANALYSIS');
  const [industryType, setIndustryType] = useState(prompt?.industryType || '');
  const [systemPrompt, setSystemPrompt] = useState(prompt?.systemPrompt || '');
  const [userPrompt, setUserPrompt] = useState(prompt?.userPrompt || '');
  const [error, setError] = useState('');

  const isEdit = !!prompt;

  // 서버 조용한 게이트의 저작 쪽 미러 —
  // 조직 「AI 작성 문체」 설정(Company.aiMeasureTone)은 본문의 {{tone}} 자리에만 들어가고,
  // 대상 유형인데 그 변수가 없으면 치환이 no-op이라 설정과 무관하게 본문 문장이 그대로 쓰인다.
  // 판정 조건(대상 유형 목록·플레이스홀더 표기·정확일치)은 shared가 정본이고 서버
  // apps/server/src/ai/prompts/measure-tone.ts와 같은 조건이다 — 여기서 다시 쓰지 말 것.
  const measureToneStatus = resolveAiMeasureToneTemplateStatus({
    promptType: type,
    systemPrompt,
    userPrompt,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        const result = await adminApi.updateAIPrompt(prompt.id, {
          name,
          systemPrompt,
          userPrompt,
        });
        return result;
      } else {
        const result = await adminApi.createAIPrompt({
          name,
          type,
          industryType: industryType || undefined,
          systemPrompt,
          userPrompt,
        });
        return result;
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error?.message || '저장에 실패했습니다.');
      }
    },
    onError: () => {
      setError('저장 중 오류가 발생했습니다.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name || !systemPrompt || !userPrompt) {
      setError('모든 필수 항목을 입력해주세요.');
      return;
    }

    mutation.mutate();
  };

  // 미저장 닫기 보호 — 기준선은 열림 시점의 값(state 초기값과 같은 식). 생성이면 빈 폼이 기준이다.
  const [initial] = useState(() => ({
    name: prompt?.name || '',
    type: prompt?.type || 'HAZARD_ANALYSIS',
    industryType: prompt?.industryType || '',
    systemPrompt: prompt?.systemPrompt || '',
    userPrompt: prompt?.userPrompt || '',
  }));
  const isDirty =
    name !== initial.name ||
    type !== initial.type ||
    industryType !== initial.industryType ||
    systemPrompt !== initial.systemPrompt ||
    userPrompt !== initial.userPrompt;

  return (
    // 저장 줄(문체 경고 + 버튼)은 종전처럼 폼 끝에 둔다 — 그 경고의 자리·조건을
    // `__tests__/aiSettingsMeasureToneMirror.test.ts`가 소스에서 읽는다.
    <Modal
      open
      onClose={onClose}
      isDirty={isDirty}
      title={
        <>
          {isEdit ? 'AI 프롬프트 수정' : 'AI 프롬프트 추가'}
          {isEdit && <span className="text-sm font-normal text-gray-500 ml-2">(새 버전 생성)</span>}
        </>
      }
      ariaLabel={isEdit ? 'AI 프롬프트 수정' : 'AI 프롬프트 추가'}
      size="xl"
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                프롬프트 이름 *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 건설현장 위험요소 분석"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                프롬프트 유형
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={isEdit}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
              >
                {Object.entries(PROMPT_TYPES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              산업 유형 (선택사항)
            </label>
            <input
              type="text"
              value={industryType}
              onChange={(e) => setIndustryType(e.target.value)}
              disabled={isEdit}
              placeholder="예: 건설업, 제조업 (비워두면 전체 산업에 적용)"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              시스템 프롬프트 *
            </label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={6}
              placeholder="AI의 역할과 응답 형식을 정의하는 프롬프트를 입력하세요."
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              변수: {'{{industryType}}'}, {'{{siteHazards}}'}, {'{{language}}'}
              {measureToneStatus !== 'NOT_TARGET' && (
                <>
                  , <span className="font-medium">{AI_MEASURE_TONE_PLACEHOLDER}</span> (조직 설정
                  「AI 작성 문체」에서 옵니다)
                </>
              )}
            </p>
            {measureToneStatus === 'MISSING_VARIABLE' && (
              <p
                data-testid="measure-tone-mirror"
                className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800"
              >
                이 유형은 조직의 「AI 작성 문체」 설정을 {AI_MEASURE_TONE_PLACEHOLDER} 변수로
                받습니다. 지금 본문에 그 변수가 없어, 조직이 어떤 문체를 골라도 반영되지 않고 이
                프롬프트에 적힌 문체 문장이 그대로 쓰입니다. 문체를 지시하는 문장을{' '}
                {AI_MEASURE_TONE_PLACEHOLDER}로 바꾸면 반영됩니다.
              </p>
            )}
            {measureToneStatus === 'APPLIED' && (
              <p
                data-testid="measure-tone-mirror"
                className="mt-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600"
              >
                조직의 「AI 작성 문체」 설정이 {AI_MEASURE_TONE_PLACEHOLDER} 자리에 들어갑니다. 이
                변수를 지우면 반영이 꺼지고, 본문에 적힌 문체 문장이 그대로 쓰입니다.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              사용자 프롬프트 템플릿 *
            </label>
            <textarea
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              rows={4}
              placeholder="이미지 분석 요청 시 사용될 사용자 프롬프트 템플릿"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
            />
          </div>

          {/*
            저장 시 경고 — 저장을 막지 않는다. 차단 대신 저장 버튼 옆에 남겨 누르는 순간 눈에
            들어오게 둔다.

            ⚠️ 막지 않는 근거를 「배포 순서상 정상 상태」로 적지 말 것 — 그 배포(P4)는 2026-09-20에
            끝났으므로 그 문장은 「순서가 끝났으니 이제 막아도 된다」로 읽힌다. 근거는 시점에 의존하지
            않는 것으로: ① 결정 2026-09-20 §7이 승인한 것은 「경고」이고 차단은 미승인 동작 변경이다
            ② `MISSING_VARIABLE`은 신규 저작의 «중간» 상태라 막으면 새 템플릿을 만들 수 없다
            (PROMPT_TYPES에 HAZARD_ANALYSIS가 있어 생성 가능한 한 참이다).
            판정·근거 전문은 shared `resolveAiMeasureToneTemplateStatus` JSDoc.
          */}
          <div className="flex items-center justify-between gap-3 pt-4">
            {/* 존재 자체가 「경고 중」이어야 한다 — 빈 채로 늘 렌더하면 DOM 검사가 오독한다. */}
            {measureToneStatus === 'MISSING_VARIABLE' ? (
              <p data-testid="measure-tone-save-warning" className="text-xs text-amber-700">
                문체 변수 없이 저장합니다 — 조직 문체 설정은 이 프롬프트에 반영되지 않습니다.
              </p>
            ) : (
              <span />
            )}
            <div className="flex gap-3">
              {/* 본문(Modal children) 안이라 base의 닫기 요청 context가 잡힌다 — dirty 확인을 거친다. */}
              <ModalCancelButton onCancel={onClose} />
              <Button type="submit" variant="primary" disabled={mutation.isPending}>
                {mutation.isPending ? '저장 중...' : isEdit ? '새 버전 저장' : '저장'}
              </Button>
            </div>
          </div>
        </form>
    </Modal>
  );
}

// ============================================
// 이력 모달
// ============================================

function HistoryModal({
  promptId,
  onClose,
  onRestore,
}: {
  promptId: string;
  onClose: () => void;
  onRestore: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ai-prompts', promptId, 'history'],
    queryFn: () => adminApi.getAIPrompt(promptId),
  });

  // 복원 확인 — 이 이력 모달(z-[70]) 위에 뜨므로 한 층 위(`SUB_CONFIRM_LAYER`).
  const [confirm, confirmDialog] = useConfirmDialog();

  const restoreMutation = useMutation({
    mutationFn: (version: number) => adminApi.restoreAIPrompt(promptId, version),
    onSuccess: (result) => {
      if (result.success) {
        onRestore();
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="버전 이력"
      size="xl"
      footer={
        <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
          닫기
        </button>
      }
    >
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">로딩 중...</div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {data?.data?.history?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">이전 버전이 없습니다.</div>
            ) : (
              data?.data?.history?.map((h: { id: string; version: number; createdAt: string; systemPrompt: string }) => (
                <div key={h.id} className="border rounded p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">버전 {h.version}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {new Date(h.createdAt).toLocaleString('ko-KR')}
                      </span>
                      <button
                        onClick={async () => {
                          if (
                            await confirm({
                              title: '버전 복원',
                              body: `버전 ${h.version}으로 복원하시겠습니까?`,
                              confirmLabel: '복원',
                              ...SUB_CONFIRM_LAYER,
                            })
                          ) {
                            restoreMutation.mutate(h.version);
                          }
                        }}
                        disabled={restoreMutation.isPending}
                        className="text-sm text-brand-600 hover:text-brand-800 disabled:opacity-50"
                      >
                        복원
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    <p className="font-medium text-gray-700 mb-1">시스템 프롬프트:</p>
                    <p className="line-clamp-3">{h.systemPrompt}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {confirmDialog}
    </Modal>
  );
}

// ============================================
// 프롬프트 상세보기 모달
// ============================================

function PromptDetailModal({
  prompt,
  onClose,
  onEdit,
}: {
  prompt: AIPrompt;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-3">
          {prompt.name}
          <span className="px-2 py-0.5 text-xs font-normal bg-brand-100 text-brand-700 rounded">
            v{prompt.version}
          </span>
          {prompt.isActive ? (
            <span className="px-2 py-0.5 text-xs font-normal bg-green-100 text-green-700 rounded">
              활성
            </span>
          ) : (
            <span className="px-2 py-0.5 text-xs font-normal bg-gray-100 text-gray-600 rounded">
              비활성
            </span>
          )}
        </span>
      }
      ariaLabel={prompt.name}
      size="xl"
      panelClassName="!max-w-3xl"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            닫기
          </button>
          <Button variant="primary" onClick={onEdit}>
            수정
          </Button>
        </>
      }
    >
        <p className="text-sm text-gray-500 mb-6">
          {PROMPT_TYPES[prompt.type]?.name || prompt.type}
          {prompt.industryType && ` • 산업유형: ${prompt.industryType}`}
        </p>

        <div className="space-y-6">
          {/* 시스템 프롬프트 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
              시스템 프롬프트
            </h4>
            <Card surface="soft" elevation="none" className="max-h-64 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                {prompt.systemPrompt}
              </pre>
            </Card>
          </div>

          {/* 사용자 프롬프트 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-brand-500 rounded-full"></span>
              사용자 프롬프트 템플릿
            </h4>
            <Card surface="soft" elevation="none" className="max-h-64 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                {prompt.userPrompt}
              </pre>
            </Card>
          </div>

          {/* 메타 정보 */}
          <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
            <div className="flex gap-4">
              <span>생성일: {new Date(prompt.createdAt).toLocaleString('ko-KR')}</span>
              <span>수정일: {new Date(prompt.updatedAt).toLocaleString('ko-KR')}</span>
            </div>
            {prompt.createdBy && <span>수정자: {prompt.createdBy}</span>}
          </div>
        </div>
    </Modal>
  );
}
